<template>
  <div class="template-manager">
    <div class="header">
      <h2>Document Template Library</h2>
      <div class="actions">
        <button class="primary-button" @click="createNewTemplate">Create New Template</button>
      </div>
    </div>

    <div class="content">
      <div class="document-types">
        <h3>Document Types</h3>
        <div class="type-list">
          <div 
            v-for="type in documentTypes" 
            :key="type.id"
            class="type-item"
            :class="{ 'selected': selectedDocumentType === type.id }"
            @click="selectDocumentType(type.id)"
          >
            <div class="type-name">{{ type.name }}</div>
            <div class="type-count">{{ getTemplateCount(type.id) }} templates</div>
          </div>
        </div>
      </div>

      <div class="template-list">
        <h3>Templates for {{ getSelectedDocumentTypeName() }}</h3>
        <div v-if="templates.length === 0" class="no-templates">
          No templates available for this document type.
        </div>
        <div v-else class="templates">
          <div 
            v-for="template in templates" 
            :key="template.id"
            class="template-item"
            :class="{ 'selected': selectedTemplate?.id === template.id }"
            @click="selectTemplate(template)"
          >
            <div class="template-header">
              <div class="template-name">{{ template.name }}</div>
              <div class="template-version">{{ template.version }}</div>
            </div>
            <div class="template-description">{{ template.description }}</div>
            <div class="template-actions">
              <button class="edit-button" @click.stop="editTemplate(template)">Edit</button>
              <button class="delete-button" @click.stop="confirmDeleteTemplate(template)">Delete</button>
            </div>
          </div>
        </div>
      </div>

      <div class="template-details" v-if="selectedTemplate">
        <h3>Template Details</h3>
        <div class="detail-section">
          <h4>Basic Information</h4>
          <div class="detail-item">
            <div class="detail-label">Name:</div>
            <div class="detail-value">{{ selectedTemplate.name }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Version:</div>
            <div class="detail-value">{{ selectedTemplate.version }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Description:</div>
            <div class="detail-value">{{ selectedTemplate.description }}</div>
          </div>
        </div>

        <div class="detail-section">
          <h4>Identifiers</h4>
          <div class="identifiers-list">
            <div v-for="(identifier, index) in selectedTemplate.identifiers" :key="index" class="identifier-item">
              {{ identifier }}
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h4>Field Mappings</h4>
          <div class="field-mappings">
            <div v-for="(mapping, field) in selectedTemplate.fieldMappings" :key="field" class="field-mapping">
              <div class="field-name">{{ formatFieldName(field) }}</div>
              <div class="field-position">
                x: {{ (mapping.x * 100).toFixed(1) }}%, 
                y: {{ (mapping.y * 100).toFixed(1) }}%, 
                w: {{ (mapping.width * 100).toFixed(1) }}%, 
                h: {{ (mapping.height * 100).toFixed(1) }}%
              </div>
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="selectedTemplate.tableMapping">
          <h4>Table Mapping</h4>
          <div class="table-mapping">
            <div class="table-range">
              Table Range: {{ (selectedTemplate.tableMapping.startY * 100).toFixed(1) }}% - {{ (selectedTemplate.tableMapping.endY * 100).toFixed(1) }}%
            </div>
            <div class="table-columns">
              <h5>Columns</h5>
              <div v-for="(column, index) in selectedTemplate.tableMapping.columns" :key="index" class="column-mapping">
                <div class="column-name">{{ formatFieldName(column.name) }}</div>
                <div class="column-position">
                  x: {{ (column.x * 100).toFixed(1) }}%, 
                  w: {{ (column.width * 100).toFixed(1) }}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Template Editor Dialog -->
    <div v-if="showTemplateEditor" class="template-editor-overlay">
      <div class="template-editor">
        <div class="editor-header">
          <h3>{{ isNewTemplate ? 'Create New Template' : 'Edit Template' }}</h3>
          <button class="close-button" @click="closeTemplateEditor">×</button>
        </div>
        <div class="editor-content">
          <div class="form-group">
            <label for="template-name">Template Name</label>
            <input type="text" id="template-name" v-model="editingTemplate.name" />
          </div>
          <div class="form-group">
            <label for="template-version">Version</label>
            <input type="text" id="template-version" v-model="editingTemplate.version" />
          </div>
          <div class="form-group">
            <label for="template-description">Description</label>
            <textarea id="template-description" v-model="editingTemplate.description"></textarea>
          </div>
          
          <div class="form-group">
            <label>Identifiers</label>
            <div class="identifiers-editor">
              <div v-for="(identifier, index) in editingTemplate.identifiers" :key="index" class="identifier-editor">
                <input type="text" v-model="editingTemplate.identifiers[index]" />
                <button @click="removeIdentifier(index)" class="remove-button">×</button>
              </div>
              <button @click="addIdentifier" class="add-button">Add Identifier</button>
            </div>
          </div>
          
          <div class="form-group">
            <label>Field Mappings</label>
            <div class="field-mappings-editor">
              <div v-for="(mapping, field) in editingTemplate.fieldMappings" :key="field" class="field-mapping-editor">
                <div class="field-name">{{ formatFieldName(field) }}</div>
                <div class="field-position-inputs">
                  <div class="position-input">
                    <label>X:</label>
                    <input type="number" v-model.number="editingTemplate.fieldMappings[field].x" step="0.01" min="0" max="1" />
                  </div>
                  <div class="position-input">
                    <label>Y:</label>
                    <input type="number" v-model.number="editingTemplate.fieldMappings[field].y" step="0.01" min="0" max="1" />
                  </div>
                  <div class="position-input">
                    <label>Width:</label>
                    <input type="number" v-model.number="editingTemplate.fieldMappings[field].width" step="0.01" min="0" max="1" />
                  </div>
                  <div class="position-input">
                    <label>Height:</label>
                    <input type="number" v-model.number="editingTemplate.fieldMappings[field].height" step="0.01" min="0" max="1" />
                  </div>
                </div>
                <button @click="removeFieldMapping(field)" class="remove-button">×</button>
              </div>
              <button @click="showAddFieldMapping = true" class="add-button">Add Field Mapping</button>
            </div>
          </div>
          
          <div v-if="editingTemplate.tableMapping" class="form-group">
            <label>Table Mapping</label>
            <div class="table-mapping-editor">
              <div class="table-range-editor">
                <div class="position-input">
                  <label>Start Y:</label>
                  <input type="number" v-model.number="editingTemplate.tableMapping.startY" step="0.01" min="0" max="1" />
                </div>
                <div class="position-input">
                  <label>End Y:</label>
                  <input type="number" v-model.number="editingTemplate.tableMapping.endY" step="0.01" min="0" max="1" />
                </div>
              </div>
              
              <label>Columns</label>
              <div v-for="(column, index) in editingTemplate.tableMapping.columns" :key="index" class="column-mapping-editor">
                <div class="column-name">{{ formatFieldName(column.name) }}</div>
                <div class="column-position-inputs">
                  <div class="position-input">
                    <label>X:</label>
                    <input type="number" v-model.number="column.x" step="0.01" min="0" max="1" />
                  </div>
                  <div class="position-input">
                    <label>Width:</label>
                    <input type="number" v-model.number="column.width" step="0.01" min="0" max="1" />
                  </div>
                </div>
                <button @click="removeTableColumn(index)" class="remove-button">×</button>
              </div>
              <button @click="showAddTableColumn = true" class="add-button">Add Table Column</button>
            </div>
          </div>
        </div>
        <div class="editor-actions">
          <button class="save-button" @click="saveTemplate">Save Template</button>
          <button class="cancel-button" @click="closeTemplateEditor">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Add Field Mapping Dialog -->
    <div v-if="showAddFieldMapping" class="dialog-overlay">
      <div class="dialog">
        <div class="dialog-header">
          <h3>Add Field Mapping</h3>
          <button class="close-button" @click="showAddFieldMapping = false">×</button>
        </div>
        <div class="dialog-content">
          <div class="form-group">
            <label for="field-name">Field Name</label>
            <input type="text" id="field-name" v-model="newFieldName" />
          </div>
          <div class="form-group">
            <label>Position</label>
            <div class="field-position-inputs">
              <div class="position-input">
                <label>X:</label>
                <input type="number" v-model.number="newFieldPosition.x" step="0.01" min="0" max="1" />
              </div>
              <div class="position-input">
                <label>Y:</label>
                <input type="number" v-model.number="newFieldPosition.y" step="0.01" min="0" max="1" />
              </div>
              <div class="position-input">
                <label>Width:</label>
                <input type="number" v-model.number="newFieldPosition.width" step="0.01" min="0" max="1" />
              </div>
              <div class="position-input">
                <label>Height:</label>
                <input type="number" v-model.number="newFieldPosition.height" step="0.01" min="0" max="1" />
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <button class="save-button" @click="addFieldMapping">Add Field</button>
          <button class="cancel-button" @click="showAddFieldMapping = false">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Add Table Column Dialog -->
    <div v-if="showAddTableColumn" class="dialog-overlay">
      <div class="dialog">
        <div class="dialog-header">
          <h3>Add Table Column</h3>
          <button class="close-button" @click="showAddTableColumn = false">×</button>
        </div>
        <div class="dialog-content">
          <div class="form-group">
            <label for="column-name">Column Name</label>
            <input type="text" id="column-name" v-model="newColumnName" />
          </div>
          <div class="form-group">
            <label>Position</label>
            <div class="column-position-inputs">
              <div class="position-input">
                <label>X:</label>
                <input type="number" v-model.number="newColumnPosition.x" step="0.01" min="0" max="1" />
              </div>
              <div class="position-input">
                <label>Width:</label>
                <input type="number" v-model.number="newColumnPosition.width" step="0.01" min="0" max="1" />
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <button class="save-button" @click="addTableColumn">Add Column</button>
          <button class="cancel-button" @click="showAddTableColumn = false">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <div v-if="showDeleteConfirmation" class="dialog-overlay">
      <div class="dialog">
        <div class="dialog-header">
          <h3>Confirm Delete</h3>
          <button class="close-button" @click="showDeleteConfirmation = false">×</button>
        </div>
        <div class="dialog-content">
          <p>Are you sure you want to delete the template "{{ templateToDelete?.name }}"?</p>
          <p>This action cannot be undone.</p>
        </div>
        <div class="dialog-actions">
          <button class="delete-button" @click="deleteTemplate">Delete</button>
          <button class="cancel-button" @click="showDeleteConfirmation = false">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import templateLibraryService from '@/services/templateLibraryService';
import documentClassificationService from '@/services/documentClassificationService';

// State
const documentTypes = ref([]);
const selectedDocumentType = ref('');
const templates = ref([]);
const selectedTemplate = ref(null);

// Template editor state
const showTemplateEditor = ref(false);
const isNewTemplate = ref(false);
const editingTemplate = ref({});

// Field mapping dialog state
const showAddFieldMapping = ref(false);
const newFieldName = ref('');
const newFieldPosition = ref({ x: 0.5, y: 0.5, width: 0.2, height: 0.03 });

// Table column dialog state
const showAddTableColumn = ref(false);
const newColumnName = ref('');
const newColumnPosition = ref({ x: 0.5, width: 0.2 });

// Delete confirmation dialog state
const showDeleteConfirmation = ref(false);
const templateToDelete = ref(null);

// Initialize data
onMounted(() => {
  loadDocumentTypes();
});

// Load document types
function loadDocumentTypes() {
  const allDocTypes = documentClassificationService.getDocumentTypes();
  documentTypes.value = allDocTypes.map(type => ({
    id: type.id,
    name: type.name
  }));
  
  if (documentTypes.value.length > 0) {
    selectDocumentType(documentTypes.value[0].id);
  }
}

// Get template count for a document type
function getTemplateCount(documentType) {
  const variations = templateLibraryService.getTemplateVariations(documentType);
  return variations.length;
}

// Select a document type
function selectDocumentType(documentType) {
  selectedDocumentType.value = documentType;
  selectedTemplate.value = null;
  loadTemplates(documentType);
}

// Get the name of the selected document type
function getSelectedDocumentTypeName() {
  const docType = documentTypes.value.find(type => type.id === selectedDocumentType.value);
  return docType ? docType.name : 'Unknown Document Type';
}

// Load templates for a document type
function loadTemplates(documentType) {
  templates.value = templateLibraryService.getTemplateVariations(documentType);
}

// Select a template
function selectTemplate(template) {
  selectedTemplate.value = template;
}

// Format field name for display
function formatFieldName(fieldName) {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

// Create a new template
function createNewTemplate() {
  isNewTemplate.value = true;
  
  // Create a new template with default values
  editingTemplate.value = {
    id: `${selectedDocumentType.value}_${Date.now()}`,
    name: `New ${getSelectedDocumentTypeName()} Template`,
    description: 'New template description',
    version: 'Custom',
    fieldMappings: {},
    identifiers: [],
    tableMapping: selectedDocumentType.value === 'g703' ? {
      startY: 0.25,
      endY: 0.85,
      columns: []
    } : null
  };
  
  showTemplateEditor.value = true;
}

// Edit an existing template
function editTemplate(template) {
  isNewTemplate.value = false;
  
  // Create a deep copy of the template to edit
  editingTemplate.value = JSON.parse(JSON.stringify(template));
  
  showTemplateEditor.value = true;
}

// Close the template editor
function closeTemplateEditor() {
  showTemplateEditor.value = false;
}

// Add a new identifier
function addIdentifier() {
  editingTemplate.value.identifiers.push('');
}

// Remove an identifier
function removeIdentifier(index) {
  editingTemplate.value.identifiers.splice(index, 1);
}

// Add a new field mapping
function addFieldMapping() {
  if (!newFieldName.value) return;
  
  editingTemplate.value.fieldMappings[newFieldName.value] = { ...newFieldPosition.value };
  
  // Reset the form
  newFieldName.value = '';
  newFieldPosition.value = { x: 0.5, y: 0.5, width: 0.2, height: 0.03 };
  showAddFieldMapping.value = false;
}

// Remove a field mapping
function removeFieldMapping(field) {
  const { [field]: removed, ...rest } = editingTemplate.value.fieldMappings;
  editingTemplate.value.fieldMappings = rest;
}

// Add a new table column
function addTableColumn() {
  if (!newColumnName.value) return;
  
  if (!editingTemplate.value.tableMapping) {
    editingTemplate.value.tableMapping = {
      startY: 0.25,
      endY: 0.85,
      columns: []
    };
  }
  
  editingTemplate.value.tableMapping.columns.push({
    name: newColumnName.value,
    x: newColumnPosition.value.x,
    width: newColumnPosition.value.width
  });
  
  // Reset the form
  newColumnName.value = '';
  newColumnPosition.value = { x: 0.5, width: 0.2 };
  showAddTableColumn.value = false;
}

// Remove a table column
function removeTableColumn(index) {
  editingTemplate.value.tableMapping.columns.splice(index, 1);
}

// Save the template
async function saveTemplate() {
  try {
    await templateLibraryService.saveTemplateVariation(selectedDocumentType.value, editingTemplate.value);
    
    // Reload templates
    loadTemplates(selectedDocumentType.value);
    
    // Select the saved template
    const savedTemplate = templates.value.find(t => t.id === editingTemplate.value.id);
    if (savedTemplate) {
      selectTemplate(savedTemplate);
    }
    
    // Close the editor
    closeTemplateEditor();
  } catch (error) {
    console.error('Error saving template:', error);
    alert('Failed to save template. Please try again.');
  }
}

// Confirm delete template
function confirmDeleteTemplate(template) {
  templateToDelete.value = template;
  showDeleteConfirmation.value = true;
}

// Delete the template
async function deleteTemplate() {
  if (!templateToDelete.value) return;
  
  try {
    await templateLibraryService.deleteTemplateVariation(
      selectedDocumentType.value, 
      templateToDelete.value.id
    );
    
    // Reload templates
    loadTemplates(selectedDocumentType.value);
    
    // Clear selection if the deleted template was selected
    if (selectedTemplate.value?.id === templateToDelete.value.id) {
      selectedTemplate.value = null;
    }
    
    // Close the confirmation dialog
    showDeleteConfirmation.value = false;
    templateToDelete.value = null;
  } catch (error) {
    console.error('Error deleting template:', error);
    alert('Failed to delete template. Please try again.');
  }
}
</script>

<style scoped>
.template-manager {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f7fa;
  border-radius: 8px;
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #ffffff;
  border-bottom: 1px solid #e1e4e8;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #24292e;
}

.content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.document-types {
  width: 250px;
  padding: 16px;
  background-color: #f6f8fa;
  border-right: 1px solid #e1e4e8;
  overflow-y: auto;
}

.type-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.type-item {
  padding: 12px;
  background-color: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.type-item:hover {
  border-color: #0366d6;
}

.type-item.selected {
  background-color: #f1f8ff;
  border-color: #0366d6;
}

.type-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.type-count {
  font-size: 12px;
  color: #6a737d;
}

.template-list {
  width: 300px;
  padding: 16px;
  background-color: #ffffff;
  border-right: 1px solid #e1e4e8;
  overflow-y: auto;
}

.no-templates {
  padding: 16px;
  color: #6a737d;
  font-style: italic;
  text-align: center;
}

.templates {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.template-item {
  padding: 12px;
  background-color: #f6f8fa;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.template-item:hover {
  border-color: #0366d6;
}

.template-item.selected {
  background-color: #f1f8ff;
  border-color: #0366d6;
}

.template-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.template-name {
  font-weight: 500;
}

.template-version {
  font-size: 12px;
  color: #6a737d;
  background-color: #e1e4e8;
  padding: 2px 6px;
  border-radius: 10px;
}

.template-description {
  font-size: 14px;
  color: #24292e;
  margin-bottom: 12px;
}

.template-actions {
  display: flex;
  gap: 8px;
}

.edit-button, .delete-button {
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
}

.edit-button {
  background-color: #0366d6;
  color: white;
  border: none;
}

.delete-button {
  background-color: #d73a49;
  color: white;
  border: none;
}

.template-details {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 24px;
  background-color: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  padding: 16px;
}

.detail-section h4 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 16px;
  color: #24292e;
}

.detail-item {
  display: flex;
  margin-bottom: 8px;
}

.detail-label {
  width: 120px;
  font-weight: 500;
}

.identifiers-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.identifier-item {
  padding: 4px 8px;
  background-color: #f1f8ff;
  border-radius: 4px;
  font-size: 14px;
}

.field-mappings {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-mapping {
  display: flex;
  justify-content: space-between;
  padding: 8px;
  background-color: #f6f8fa;
  border-radius: 4px;
}

.field-name {
  font-weight: 500;
}

.table-mapping {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.table-range {
  padding: 8px;
  background-color: #f6f8fa;
  border-radius: 4px;
}

.table-columns h5 {
  margin-top: 0;
  margin-bottom: 8px;
}

.column-mapping {
  display: flex;
  justify-content: space-between;
  padding: 8px;
  background-color: #f6f8fa;
  border-radius: 4px;
  margin-bottom: 4px;
}

/* Template Editor Styles */
.template-editor-overlay, .dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.template-editor {
  width: 800px;
  max-width: 90%;
  max-height: 90vh;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e1e4e8;
}

.editor-header h3 {
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6a737d;
}

.editor-content {
  padding: 16px;
  overflow-y: auto;
  max-height: 70vh;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  font-size: 14px;
}

.form-group textarea {
  min-height: 80px;
  resize: vertical;
}

.identifiers-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.identifier-editor {
  display: flex;
  gap: 8px;
}

.field-mappings-editor, .table-mapping-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-mapping-editor, .column-mapping-editor {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background-color: #f6f8fa;
  border-radius: 4px;
}

.field-name {
  width: 150px;
  font-weight: 500;
}

.field-position-inputs, .column-position-inputs, .table-range-editor {
  display: flex;
  gap: 8px;
  flex: 1;
}

.position-input {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.position-input label {
  font-size: 12px;
  margin-bottom: 4px;
}

.position-input input {
  width: 100%;
}

.remove-button {
  background-color: #d73a49;
  color: white;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.add-button {
  padding: 8px;
  background-color: #2ea44f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  align-self: flex-start;
}

.editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid #e1e4e8;
}

.save-button, .cancel-button {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.save-button {
  background-color: #2ea44f;
  color: white;
  border: none;
}

.cancel-button {
  background-color: #f6f8fa;
  color: #24292e;
  border: 1px solid #e1e4e8;
}

.primary-button {
  padding: 8px 16px;
  background-color: #2ea44f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

/* Dialog Styles */
.dialog {
  width: 400px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e1e4e8;
}

.dialog-header h3 {
  margin: 0;
}

.dialog-content {
  padding: 16px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid #e1e4e8;
}
</style>